/**
 * Module-level cache for the checklist-scan picker. The pane unmounts whenever
 * the user switches back to the chat (App.tsx renders chat OR scan, never both),
 * so its React state would otherwise be lost. We mirror the picker state here so
 * query, filters and selections survive the round-trip — and cached items avoid
 * a reload spinner on every return. Lives for the tab session; not persisted.
 */
import type { ChecklistItem } from './types'

/** Archived scope — mirrors ramp-web-app's Is Archived radio (all/true/false). */
export type ArchivedFilter = 'active' | 'archived' | 'all'

/** Partial (`cn`) / Exact (`in`) — mirrors web FilterBar matchMode for HASH_SET fields. */
export type MatchMode = 'cn' | 'in'

/**
 * Picker filters, mirroring ramp-web-app's ChecklistItemSelectorDialog. `archived`
 * is applied server-side (archived items aren't fetched by default); the rest are
 * applied client-side over the loaded items.
 */
export interface ScanFilters {
  archived: ArchivedFilter
  names: string[]
  topics: string[]
  /** Match mode per HASH_SET field (names / topics). Defaults to Partial (`cn`). */
  matchModes: Partial<Record<'names' | 'topics', MatchMode>>
  types: string[]
  states: string[]
  scopes: string[]
  typeOfInsuranceGroupIds: string[]
  businessTypes: string[]
  typeOfInsurances: string[]
  subTypeOfInsurances: string[]
}

export const EMPTY_FILTERS: ScanFilters = {
  archived: 'active',
  names: [],
  topics: [],
  matchModes: {},
  types: [],
  states: [],
  scopes: [],
  typeOfInsuranceGroupIds: [],
  businessTypes: [],
  typeOfInsurances: [],
  subTypeOfInsurances: []
}

interface ScanCache {
  items: ChecklistItem[]
  /** True once items have been fetched at least once this session. */
  loaded: boolean
  query: string
  filters: ScanFilters
  selectedIds: string[]
}

let cache: ScanCache = {
  items: [],
  loaded: false,
  query: '',
  filters: EMPTY_FILTERS,
  selectedIds: []
}

/** Merge partial/legacy filter objects onto EMPTY_FILTERS (session cache may predate new fields). */
export const normalizeFilters = (filters: Partial<ScanFilters> | undefined): ScanFilters => ({
  ...EMPTY_FILTERS,
  ...filters,
  matchModes: { ...EMPTY_FILTERS.matchModes, ...filters?.matchModes },
  names: filters?.names ?? [],
  topics: filters?.topics ?? [],
  types: filters?.types ?? [],
  states: filters?.states ?? [],
  scopes: filters?.scopes ?? [],
  typeOfInsuranceGroupIds: filters?.typeOfInsuranceGroupIds ?? [],
  businessTypes: filters?.businessTypes ?? [],
  typeOfInsurances: filters?.typeOfInsurances ?? [],
  subTypeOfInsurances: filters?.subTypeOfInsurances ?? []
})

export const getScanCache = (): ScanCache => ({
  ...cache,
  filters: normalizeFilters(cache.filters)
})

export const patchScanCache = (patch: Partial<ScanCache>): void => {
  cache = {
    ...cache,
    ...patch,
    ...(patch.filters ? { filters: normalizeFilters(patch.filters) } : {})
  }
}

/** Count of active (non-default) filters, for the filter-toggle badge. */
export const activeFilterCount = (filters: ScanFilters): number =>
  (filters.archived !== 'active' ? 1 : 0) +
  filters.names.length +
  filters.topics.length +
  filters.types.length +
  filters.states.length +
  filters.scopes.length +
  filters.typeOfInsuranceGroupIds.length +
  filters.businessTypes.length +
  filters.typeOfInsurances.length +
  filters.subTypeOfInsurances.length
